import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { BehaviorSubject, combineLatest, fromEvent, ReplaySubject } from 'rxjs';
import { FramePickerService } from '@ui/look-book-front/print-frames/print-frames-front/frame-picker.service';
import { first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { UntypedFormArray } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import {
  addPrintFrameAction,
  changePrintFrameCountAction,
  deletePrintFrameAction,
} from '@ui/products/shopping-cart-back/actions/actions';
import cuid from 'cuid';
import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';
import { ZalamoObject } from 'ZalamoLibrary/src/lib/zalamo/pipes/names/graphql';
import { ZalamoDialogService } from '@ui/zalamo-dialog/zalamo-dialog.service';
import { PrintFramePickerFullScreenComponent } from '@ui/look-book-front/print-frames/print-frame-picker-full-screen/print-frame-picker-full-screen.component';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { gsap } from 'gsap';
import { selectPrintFramesInBasket } from '@ui/products/shopping-cart-back/actions/selector';

@Component({
    selector: 'lib-print-frames-picker',
    templateUrl: './print-frames-picker.component.html',
    styleUrls: ['./print-frames-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PrintFramesPickerComponent
  extends AliveState
  implements OnInit, OnDestroy
{
  @Input() set printSizeId(printSizeId: string) {
    this.printSizeId$.next(printSizeId);
  }

  @Input() ignoreImage = false;

  @Input() set ratio(ratio: number) {
    this.ratio$.next(ratio);
  }

  @Input() set photoId(photoId: string) {
    this.photoId$.next(photoId);
  }

  constructor(
    public framePickerService: FramePickerService,
    private elementRef: ElementRef,
    private store: Store,
    private zalamoDialogService: ZalamoDialogService
  ) {
    super();
  }

  @Input() @Required context: ZalamoObject;
  @Input() @Required contextId: string;
  firstSort = [];
  optionsModel = new UntypedFormArray([]);
  printSizeId$ = new ReplaySubject<string>(1);
  ratio$ = new ReplaySubject<number>(1);
  photoId$ = new ReplaySubject<string>(1);
  options$ = this.printSizeId$.pipe(
    switchMap((printSizeId) =>
      this.framePickerService.getOptionsForSize(printSizeId)
    ),
    map((options) => {
      if (this.firstSort.length === 0) {
        this.firstSort = options.map((option) => JSON.stringify(option.value));
      }
      // console.log(this.firstSort);
      return options.sort((a, b) => {
        return (
          this.firstSort.indexOf(JSON.stringify(a.value)) -
          this.firstSort.indexOf(JSON.stringify(b.value))
        );
      });
    })
  );
  scrollableLeft$ = new BehaviorSubject(false);
  scrollableRight = new BehaviorSubject(false);
  @ViewChild('container', { static: true }) container: ElementRef<HTMLElement>;

  domObserver: MutationObserver;

  remove(value) {
    combineLatest([
      this.printSizeId$,
      this.ratio$,
      this.photoId$,
      this.store.pipe(select(selectPrintFramesInBasket)),
    ])
      .pipe(
        first(),
        tap(([printSizeId, ratio, photoId, printFramesInBasket]) => {
          const frame = printFramesInBasket.find(
            (frame) =>
              frame.photoId === photoId &&
              frame.printFormatId === printSizeId &&
              frame.value === JSON.stringify(value)
          );
          if (frame.count === 1) {
            this.store.dispatch(
              deletePrintFrameAction({
                photoId,
                printFrameId: frame.printFrameId,
              })
            );
          } else {
            this.store.dispatch(
              changePrintFrameCountAction({
                photoId,
                printFrameId: frame.printFrameId,
                count: frame.count - 1,
              })
            );
          }
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.domObserver = new MutationObserver((list) => {
      this.scrollableRight.next(
        this.container.nativeElement.scrollLeft +
          this.container.nativeElement.clientWidth <
          this.container.nativeElement.scrollWidth
      );
    });
    this.domObserver.observe(this.container.nativeElement, {
      childList: true,
      subtree: true,
    });

    this.subscribeWhileAlive(
      fromEvent(this.container.nativeElement, 'scroll').pipe(
        tap((event) => {
          const viewPort = this.container.nativeElement.clientWidth;

          this.scrollableRight.next(
            this.container.nativeElement.scrollLeft +
              this.container.nativeElement.clientWidth <
              this.container.nativeElement.scrollWidth
          );
          this.scrollableLeft$.next(
            this.container.nativeElement.scrollLeft > 0
          );
        })
      )
    );
  }

  ngOnDestroy() {
    this.domObserver.disconnect();
  }

  goRight() {
    const viewPort = this.container.nativeElement.getBoundingClientRect().width;
    gsap.to(this.container.nativeElement, {
      scrollLeft: this.container.nativeElement.scrollLeft + viewPort * 0.8,
      duration: 0.2,
      delay: 0,
    });

    this.scrollableLeft$.next(true);

    this.scrollableRight.next(
      this.container.nativeElement.scrollLeft +
        this.container.nativeElement.clientWidth <
        this.container.nativeElement.scrollWidth
    );
  }

  goLeft() {
    const viewPort = this.container.nativeElement.getBoundingClientRect().width;
    gsap.to(this.container.nativeElement, {
      scrollLeft: this.container.nativeElement.scrollLeft - viewPort * 0.8,
      duration: 0.2,
      delay: 0,
    });
    this.scrollableLeft$.next(
      this.container.nativeElement.scrollLeft - viewPort * 0.8 > 0
    );
    this.scrollableRight.next(true);
  }

  add(value, price) {
    combineLatest([
      this.printSizeId$,
      this.ratio$,
      this.photoId$,
      this.store.pipe(select(selectPrintFramesInBasket)),
    ])
      .pipe(
        first(),
        tap(([printSizeId, ratio, photoId, printFramesInBasket]) => {
          const frame = printFramesInBasket.find(
            (frame) =>
              frame.photoId === photoId &&
              frame.printFormatId === printSizeId &&
              frame.value === JSON.stringify(value)
          );
          if (frame) {
            this.store.dispatch(
              changePrintFrameCountAction({
                photoId,
                printFrameId: frame.printFrameId,
                count: frame.count + 1,
              })
            );
          } else {
            this.store.dispatch(
              addPrintFrameAction({
                printFrame: {
                  printFormatId: printSizeId,
                  ratio,
                  photoId,
                  value: JSON.stringify(value),
                  price,
                  printFrameId: cuid(),
                },
              })
            );
          }
        })
      )
      .subscribe();
  }

  trackFrame(index, frame) {
    return JSON.stringify(frame.value);
  }

  openFrame(config) {
    combineLatest([this.photoId$, this.ratio$, this.printSizeId$])
      .pipe(
        first(),
        tap(([photoId, ratio, printSizeId]) => {
          const ref = this.zalamoDialogService.openFull(
            PrintFramePickerFullScreenComponent,

            {
              isBare: true,
            }
          );
          ref.componentInstance.photoId = photoId;
          ref.componentInstance.currentFrame = config;
          ref.componentInstance.context = this.context;
          ref.componentInstance.contextId = this.contextId;
          ref.componentInstance.ratio = ratio;
          ref.componentInstance.printSizeId = printSizeId;

          ref.componentInstance.close
            .pipe(
              takeUntil(ref.afterClosed()),
              tap(() => ref.close())
            )
            .subscribe();
        })
      )
      .subscribe();
  }
}
